<template>
  <v-app>
    <!-- Navigation Drawer for Categories -->
    <v-navigation-drawer
      v-model="drawer"
      :temporary="isMobile"
      :permanent="!isMobile"
      class="kb-sidebar"
      :style="sidebarStyle"
    >
      <v-list>
        <v-list-item
          v-for="category in categories"
          :key="category.id"
          :value="category.id"
          :href="category.path"
          :active="category.id === activeCategoryId"
          color="primary"
        >
          <template v-slot:prepend>
            <v-icon>{{ getIcon(category.icon) }}</v-icon>
          </template>
          <v-list-item-title>{{ category.name }}</v-list-item-title>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <v-list-item
          :href="knowledgebasePath"
          color="primary"
        >
          <template v-slot:prepend>
            <v-icon>mdi-view-grid</v-icon>
          </template>
          <v-list-item-title>{{ allCategoriesLabel }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main>
      <v-container fluid class="kb-container">
        <!-- Mobile Header with Menu Toggle -->
        <div v-if="isMobile" class="kb-mobile-header">
          <v-app-bar-nav-icon @click="drawer = !drawer" class="mr-2"></v-app-bar-nav-icon>
        </div>

        <!-- Breadcrumbs -->
        <v-breadcrumbs :items="breadcrumbs" class="kb-breadcrumbs px-0">
          <template v-slot:prepend>
            <v-icon icon="mdi-home" class="mr-2"></v-icon>
          </template>
          <template v-slot:divider>
            <v-icon icon="mdi-chevron-right" size="small"></v-icon>
          </template>
        </v-breadcrumbs>

        <!-- Page Title -->
        <h1 class="text-h4 font-weight-bold mb-4">{{ pageTitle }}</h1>

        <!-- Search Bar -->
        <div v-if="showSearch" class="kb-search-wrapper">
          <v-text-field
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            prepend-inner-icon="mdi-magnify"
            variant="solo"
            flat
            hide-details
            clearable
            density="comfortable"
            class="kb-search-field"
            bg-color="grey-lighten-4"
            @keydown.enter="onSearchEnter"
          >
          </v-text-field>
        </div>

        <!-- Page-specific content -->
        <slot :searchQuery="searchQuery"></slot>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'KnowledgebaseLayout',
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    knowledgebasePath: {
      type: String,
      default: '/categories'
    },
    breadcrumbs: {
      type: Array,
      default: () => []
    },
    pageTitle: {
      type: String,
      default: ''
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    allCategoriesLabel: {
      type: String,
      default: 'All Categories'
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    searchAction: {
      type: String,
      default: ''
    },
    activeCategoryId: {
      type: Number,
      default: null
    },
    initialSearchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      drawer: false,
      windowWidth: 0,
      scrollY: 0,
      searchQuery: ''
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 960
    },
    sidebarOffset() {
      return Math.max(0, 102 - this.scrollY)
    },
    sidebarStyle() {
      return {
        top: this.sidebarOffset + 'px',
        height: 'calc(100vh - ' + this.sidebarOffset + 'px)'
      }
    }
  },
  mounted() {
    this.searchQuery = this.initialSearchQuery || ''
    this.drawer = !this.isMobile
    this.windowWidth = window.innerWidth
    this.scrollY = window.scrollY
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getIcon(iconName) {
      if (!iconName) return 'mdi-folder'
      return iconName.startsWith('mdi-') ? iconName : `mdi-${iconName}`
    },
    handleResize() {
      this.windowWidth = window.innerWidth
      this.drawer = !this.isMobile
    },
    handleScroll() {
      this.scrollY = window.scrollY
    },
    onSearchEnter() {
      if (this.searchAction && this.searchQuery && this.searchQuery.trim()) {
        window.location = this.searchAction + '?q=' + encodeURIComponent(this.searchQuery.trim())
      }
    }
  }
}
</script>

<style scoped>
/* Sidebar */
.kb-sidebar {
  position: fixed !important;
  left: 0 !important;
  width: 256px !important;
  border-top: 1px solid #e0e0e0 !important;
  z-index: 2001 !important;
}

.kb-sidebar :deep(.v-list) {
  padding-top: 20px;
}

/* Main Container */
.kb-container {
  max-width: 1400px;
  padding: 0 24px 24px 24px;
}

.kb-container :deep(.v-container) {
  max-width: 100%;
}

/* Mobile Header */
.kb-mobile-header {
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 16px;
}

/* Breadcrumbs */
.kb-breadcrumbs {
  font-size: 14px;
  margin-bottom: 16px;
}

/* Search */
.kb-search-wrapper {
  max-width: 400px;
  margin-bottom: 32px;
}

.kb-search-field :deep(.v-field) {
  border-radius: 12px;
}

/* Remove underline from all links */
a {
  text-decoration: none;
}

a:hover {
  text-decoration: none !important;
}

/* Responsive */
@media (max-width: 959px) {
  .kb-container {
    padding: 0 16px 16px 16px;
  }
}

@media (max-width: 599px) {
  .kb-container {
    padding: 0 12px 12px 12px;
  }

  .kb-search-wrapper {
    max-width: 100%;
  }
}
</style>
